import { Component } from "react"

/* Components */
import { InnerLayout } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Helpers */
import { utils } from "../../helpers"

/* Widgets */
import StudioVideo from "./StudioVideo"
import StudioRoom from "./StudioRoom"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { view } from "../../api/Livekit"

/* Icons */
import {
    CloseIcon, BroadcastIcon, UsersIcon, VideoIcon, AudioIcon, UploadIcon, DownloadIcon,
    SpeedIcon, CpuIcon, ServerIcon, ArchiveIcon, RefreshIcon, ArrowIcon, EyeIcon,
} from "./StudioIcons"


/* HOW OFTEN THE NUMBERS ARE READ AGAIN */
const INTERVAL = 15000


/* Widget Studio Stream */
class StudioStream extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            serviceURL: "",
            recordURL: "",
            name: "",
            data: null,
            rooms: [],
            status: "loading",
        }

        this._timer = null
    }

    /* Open Action */
    open = ({ serviceURL, recordURL, name }) => {

        this.setState({ visible: true, serviceURL, recordURL, name: utils.checkValue(name, "") }, () => {

            this.load()

            this._timer = setInterval(() => {
                this.check()
            }, INTERVAL)
        })
    }

    /* Close */
    close = () => {

        this.setState({ visible: false, serviceURL: "", recordURL: "", name: "", status: "loading", data: null, rooms: [] })

        if (this._timer) {
            clearInterval(this._timer)
            this._timer = null
        }
    }

    componentWillUnmount = () => {

        /* Check Timer */
        if (this._timer) {
            clearInterval(this._timer)
            this._timer = null
        }
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { serviceURL } = this.state

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        view(serviceURL).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data, rooms: response.data._rooms })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: null, rooms: [] })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: null, rooms: [] })
            }
            else {
                this.setState({ status: "error", data: null, rooms: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: null, rooms: [] })
        })
    }

    /* Check Action — the numbers are refreshed without the loader */
    check = () => {

        /* Fields */
        const { serviceURL } = this.state

        /* Send to REST API */
        view(serviceURL).then((response) => {
            if (response.status === 200) {
                this.setState({ data: response.data, rooms: response.data._rooms })
            }
        })
    }

    /* Draw One Metric */
    _metric = (icon, label, value, options = {}) => (
        <div className={`std--metric ${options.tone ? options.tone : ""}`}>

            <div className="std--metric-icon">{icon}</div>

            <div className="std--metric-text">
                <div className="std--metric-label">{label}</div>
                <div className={`std--metric-value ${options.small ? "small" : ""}`}>{value}</div>
                {options.note ? <div className="std--metric-note">{options.note}</div> : null}
            </div>

        </div>
    )

    /* Draw Metrics */
    _metrics = () => {

        /* Fields */
        const { data, recordURL } = this.state

        /* Fields — the load of the machine turns the card red */
        const overloaded = utils.checkBoolean(data.cpu?.overloaded)

        return (
            <div className="std--metrics">

                {this._metric(<BroadcastIcon size={17} />, t("studio.streams"), utils.checkValue(data.rooms, 0), { tone: "blue" })}
                {this._metric(<UsersIcon size={17} />, t("studio.participants"), utils.checkValue(data.viewers, 0), { tone: "green" })}
                {this._metric(<VideoIcon size={17} />, t("studio.video"), utils.checkValue(data.tracks?.video, 0))}
                {this._metric(<AudioIcon size={17} />, t("studio.audio"), utils.checkValue(data.tracks?.audio, 0))}

                {this._metric(<UploadIcon size={17} />, "Upload", utils.checkValue(data.traffic?.rx, "—"), { small: true, note: t("studio.allTime") })}
                {this._metric(<DownloadIcon size={17} />, "Download", utils.checkValue(data.traffic?.tx, "—"), { small: true, note: t("studio.allTime") })}
                {this._metric(<SpeedIcon size={17} />, "Upload Speed", utils.checkValue(data.traffic?.rxSpeed, "—"), { small: true })}
                {this._metric(<SpeedIcon size={17} />, "Download Speed", utils.checkValue(data.traffic?.txSpeed, "—"), { small: true })}

                {this._metric(<CpuIcon size={17} />, "CPU 1 min", utils.checkValue(data.cpu?.load1m, "—"), { small: true })}
                {this._metric(<CpuIcon size={17} />, "CPU 5 min", utils.checkValue(data.cpu?.load5m, "—"), { small: true })}
                {this._metric(<CpuIcon size={17} />, "CPU 15 min", utils.checkValue(data.cpu?.load15m, "—"), { small: true })}
                {this._metric(<ServerIcon size={17} />, "CPU Cores", utils.checkValue(data.cpu?.cores, "—"), { small: true })}

                {this._metric(
                    <ServerIcon size={17} />,
                    t("studio.load"),
                    overloaded ? t("studio.yes") : t("studio.no"),
                    { small: true, tone: overloaded ? "red" : "green" },
                )}

                <a className="std--metric link" href={utils.checkValue(recordURL, "")} target="blank" rel="noreferrer">

                    <div className="std--metric-icon"><ArchiveIcon size={17} /></div>

                    <div className="std--metric-text">
                        <div className="std--metric-label">{t("studio.archive")}</div>
                        <div className="std--metric-value small">{t("studio.openLink")}</div>
                    </div>

                    <div className="std--metric-arrow"><ArrowIcon size={14} /></div>

                </a>

            </div>
        )
    }

    /* Draw Rooms */
    _rooms = () => {

        /* Fields */
        const { rooms, data, serviceURL } = this.state

        /* Check Rooms */
        if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
            return (
                <div className="std--section">
                    <div className="std--chips-empty">{t("studio.noStreams")}</div>
                </div>
            )
        }

        return (
            <div className="std--section">

                <div className="std--section-head">
                    <div className="std--section-title">
                        <VideoIcon size={13} />
                        {t("studio.liveStreams")}
                        <span className="std--counter">{rooms.length}</span>
                    </div>
                </div>

                <div className="std--videos">
                    {rooms.map((item, index) =>
                        <div className="std--video" key={`${index}`}>

                            <div className="std--video-frame">
                                <StudioVideo socketURL={utils.checkValue(data.socketURL, "")} token={utils.checkValue(item.token, "")} />
                                <div className="std--video-live"><i />LIVE</div>
                            </div>

                            <div onClick={() => this._room.open({ serviceURL, room: utils.checkValue(item.name, "") })} className="std--video-info">

                                <div className="std--video-name">{utils.checkValue(item.name, "—")}</div>

                                <div className="std--video-viewers">
                                    <EyeIcon size={13} />
                                    <span>{utils.checkValue(item.viewers, 0)}</span>
                                </div>

                                <div className="std--video-arrow"><ArrowIcon size={14} /></div>

                            </div>

                        </div>
                    )}
                </div>

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status } = this.state

        /* Layout */
        if (status !== "ok") {
            return <InnerLayout status={status} reload={() => this.load(true)} />
        }

        return (
            <>
                {/* Draw Metrics */}
                {this._metrics()}

                {/* Draw Rooms */}
                {this._rooms()}
            </>
        )
    }

    render = () => {

        /* Fields */
        const { visible, name, status } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="std--modal wide"
                overlayClassName="std--overlay"
                closeTimeoutMS={200}
            >

                <div className="std--modal-header">

                    <div className="std--modal-icon live">
                        <BroadcastIcon size={18} />
                    </div>

                    <div className="std--modal-head-text">
                        <h2>{t("studio.streamTitle")}</h2>
                        <p>{name !== "" ? name : t("studio.streamSubtitle")}</p>
                    </div>

                    {status === "ok" &&
                        <div onClick={() => this.load(true)} className="std--modal-refresh" title={t("studio.refresh")}>
                            <RefreshIcon size={15} />
                        </div>
                    }

                    <div onClick={() => this.close()} className="std--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                <div className="std--modal-body">
                    {this._content()}
                </div>

                {/* Draw Room */}
                <StudioRoom ref={(ref) => this._room = ref} />

            </Modal>
        )
    }
}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(StudioStream)
